import { useEffect, useState } from 'react'
import { css } from '@emotion/react'

import { SMALL_BREAKPOINT } from './breakpoints'

// Canonical mobile breakpoint helpers. The mobile cutoff is exclusive of
// SMALL_BREAKPOINT itself — `${SMALL_BREAKPOINT - 1}px` is the max-width
// for the mobile range. Anything at or above SMALL_BREAKPOINT is desktop.
//
// Use these helpers instead of redefining matchMedia / display-toggle CSS
// per file — it keeps the boundary aligned everywhere.

export const MOBILE_MEDIA_QUERY = `(max-width: ${SMALL_BREAKPOINT - 1}px)`

// SSR-safe matchMedia hook. Returns `false` on first render (so server
// markup matches the desktop default), then resolves to the real value
// once mounted in the browser.
export const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_MEDIA_QUERY)
    setIsMobile(mql.matches)
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])
  return isMobile
}

// `display: none` below the mobile cutoff. For elements that only render
// on desktop (stage chrome, multi-column layouts, side sketches, etc).
export const hideOnMobileCss = css`
  @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
    display: none;
  }
`

// Same as hideOnMobileCss but with !important — needed when the target
// has an inline `display` style attribute that would otherwise win.
export const hideOnMobileImportantCss = css`
  @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
    display: none !important;
  }
`

// `display: none` at or above the mobile cutoff. On mobile the element
// reverts to `display: block`. For other displays (`flex`, `grid`),
// override with an additional rule.
export const hideOnDesktopCss = css`
  display: none;
  @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
    display: block;
  }
`

// Variant for elements that should be `display: flex` on mobile.
export const hideOnDesktopFlexCss = css`
  display: none;
  @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
    display: flex;
  }
`

// Readability aliases.
export const desktopOnlyCss = hideOnMobileCss
export const desktopOnlyImportantCss = hideOnMobileImportantCss
export const mobileOnlyCss = hideOnDesktopCss
export const mobileOnlyFlexCss = hideOnDesktopFlexCss
